import { useCallback, useEffect } from 'react';
import { AUTO_PRICE_REFRESH_POLL_MS } from '../dashboardUtils';
import { getRiskCommandsOrThrow } from '../orchestration/riskCommandRef';
import { RISK_INVALIDATION_CAUSES } from '../orchestration/useRiskOrchestrationCommands';

export default function usePriceRefreshFlow({
    userUid,
    autoRefreshEnabled,
    autoRefreshEnabledRef,
    matricesLoading,
    riskCommandsRef,
    refreshPriceDataRef,
    coreLoadInFlightRef,
    exclusiveCoreLoadOwnerRef,
    priceRefreshInFlightRef,
    importApplyInFlightRef,
    posSizingAutoSyncEnabledRef,
    queuePriceRefresh,
    consumeQueuedPriceRefresh,
    markPendingRefreshFromLoadResult,
    requestPosSizingContextUpdate,
    fetchPriceRefreshStatus,
    notifyPendingPriceRefresh,
    setPriceRefreshLoading,
    setPendingAutoRefresh,
    posSizingIdioVolSource,
    forceNextMatricesReloadRef,
}) {
    const refreshPriceDependentData = useCallback(async ({
        syncPosSizingDraft = posSizingAutoSyncEnabledRef.current,
        reason = 'price_refresh',
    } = {}) => {
        if (exclusiveCoreLoadOwnerRef.current) {
            return;
        }
        if (coreLoadInFlightRef.current || priceRefreshInFlightRef.current) {
            queuePriceRefresh({ syncPosSizingDraft, reason });
            return;
        }

        const commands = getRiskCommandsOrThrow(
            riskCommandsRef,
            'usePriceRefreshFlow.refreshPriceDependentData',
            [
                'invalidateRiskSurface',
                'refreshRiskSurface',
                'refreshSelectedOverview',
            ],
        );
        // The import awaits price stabilization before its one mandatory child
        // handoff. Waiting for a child check here would wait on this same owner.
        const importOwnsRefresh = Boolean(importApplyInFlightRef.current);
        priceRefreshInFlightRef.current = true;
        setPriceRefreshLoading(true);
        try {
            const forceMatricesReload = Boolean(forceNextMatricesReloadRef?.current);
            commands.invalidateRiskSurface({
                cause: RISK_INVALIDATION_CAUSES.PRICE_WATERMARK_CHANGED,
                reason,
            });
            const loadResult = await commands.refreshRiskSurface({
                    reason,
                    // A portfolio import owns the selected Overview until it
                    // finishes, so a price refresh inside it leaves it alone.
                    includeSelectedOverview: !importApplyInFlightRef.current,
                    requireFreshMatrices: forceMatricesReload,
                });
            if (forceMatricesReload && forceNextMatricesReloadRef) {
                forceNextMatricesReloadRef.current = false;
            }
            if (markPendingRefreshFromLoadResult(loadResult)) {
                // One follow-up load is enough to close the watermark bracket.
                // If prices continue changing during that follow-up, the next
                // scheduled poll will pick them up without an immediate loop.
                if (
                    (autoRefreshEnabledRef?.current ?? autoRefreshEnabled)
                    && reason !== 'stabilize_price_watermark'
                ) {
                    queuePriceRefresh({
                        syncPosSizingDraft: posSizingAutoSyncEnabledRef.current,
                        reason: 'stabilize_price_watermark',
                    });
                }
                // Even while app prices are settling, compare the child in this
                // same cycle. Its captured market evidence is independent.
            }
            if (!importOwnsRefresh && !importApplyInFlightRef.current) {
                await requestPosSizingContextUpdate({
                    matricesOverride: loadResult?.matricesOverride ?? null,
                    idioVolSource: posSizingIdioVolSource,
                    reason,
                    checkOnly: !syncPosSizingDraft,
                });
            }
        } catch (error) {
            console.error('Error refreshing price-dependent data:', error);
        } finally {
            priceRefreshInFlightRef.current = false;
            setPriceRefreshLoading(false);
            const queuedOptions = consumeQueuedPriceRefresh();
            if (queuedOptions) {
                if (importOwnsRefresh || importApplyInFlightRef.current) {
                    // Complete the existing bounded watermark follow-up before
                    // returning to the import's final context reconciliation.
                    await refreshPriceDataRef.current?.({ ...queuedOptions, syncPosSizingDraft: false });
                } else {
                    // The queued refresh finishes with its own selected Overview refresh.
                    void refreshPriceDataRef.current?.(queuedOptions);
                }
            } else if (!importOwnsRefresh && !importApplyInFlightRef.current) {
                commands.refreshSelectedOverview({ reason });
            }
        }
    }, [
        autoRefreshEnabled,
        autoRefreshEnabledRef,
        consumeQueuedPriceRefresh,
        coreLoadInFlightRef,
        exclusiveCoreLoadOwnerRef,
        forceNextMatricesReloadRef,
        importApplyInFlightRef,
        markPendingRefreshFromLoadResult,
        posSizingAutoSyncEnabledRef,
        posSizingIdioVolSource,
        priceRefreshInFlightRef,
        queuePriceRefresh,
        refreshPriceDataRef,
        requestPosSizingContextUpdate,
        riskCommandsRef,
        setPriceRefreshLoading,
    ]);

    const refreshPriceData = useCallback(
        (options) => refreshPriceDependentData(options),
        [refreshPriceDependentData],
    );
    refreshPriceDataRef.current = refreshPriceData;

    useEffect(() => {
        if (!userUid) return;

        let cancelled = false;
        let timeoutId = null;

        const scheduleNext = () => {
            if (cancelled) return;
            timeoutId = window.setTimeout(runPoll, AUTO_PRICE_REFRESH_POLL_MS);
        };

        const runPoll = async () => {
            if (cancelled) return;
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                scheduleNext();
                return;
            }
            if (
                importApplyInFlightRef.current ||
                priceRefreshInFlightRef.current ||
                coreLoadInFlightRef.current ||
                exclusiveCoreLoadOwnerRef.current ||
                matricesLoading
            ) {
                scheduleNext();
                return;
            }

            const status = await fetchPriceRefreshStatus({ silent: true });
            if (cancelled || exclusiveCoreLoadOwnerRef.current) {
                scheduleNext();
                return;
            }

            if (status?.has_newer_prices) {
                setPendingAutoRefresh(status);

                if (autoRefreshEnabled) {
                    await refreshPriceData({
                        reason: 'auto_price_refresh',
                    });
                } else {
                    notifyPendingPriceRefresh(status);
                }
            } else {
                setPendingAutoRefresh(null);
            }

            if (!cancelled && !(status?.has_newer_prices && autoRefreshEnabled)) {
                await requestPosSizingContextUpdate({ reason: 'parent_context_poll' });
            }
            scheduleNext();
        };

        const handleVisibilityChange = () => {
            if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
                return;
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            void runPoll();
        };

        // The core bootstrap's deferred completion owns the initial price
        // watermark bracket. Polling immediately here races that baseline with
        // `since_updated_at=null`, which makes existing stored prices look new
        // and launches a redundant full risk-surface refresh after sign-in.
        scheduleNext();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelled = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [
        autoRefreshEnabled,
        coreLoadInFlightRef,
        exclusiveCoreLoadOwnerRef,
        fetchPriceRefreshStatus,
        importApplyInFlightRef,
        matricesLoading,
        notifyPendingPriceRefresh,
        priceRefreshInFlightRef,
        refreshPriceData,
        requestPosSizingContextUpdate,
        setPendingAutoRefresh,
        userUid,
    ]);

    return {
        refreshPriceData,
    };
}
